import React, { useCallback, useEffect, useState } from 'react';
import { deleteUser, getSingleUserById } from '../../server/server';
import { ReactComponent as TargetUndo } from '../../assets/svg/undoTarget.svg';
import { useNavigate, useParams } from 'react-router';
import { FaCreditCard, FaEdit, FaTrash, FaUser } from 'react-icons/fa';
import { buddyFaces } from '../../utils/BuddyConstants';
import ChangeSubscriptionModal from './ChangeSubscriptionModal';
import CreateEditUserModal from './CreateEditUserModal';
import ConfirmationModal from '../modals/ConfirmationModal';
import UserOnboardingProfileModal from './UserOnboardingProfileModal';
import UserPlanModal from './UserPlanModal';
import UserJourneyPreview from './UserJourneyPreview';

interface UserProfile {
	id: string;
	email: string;
	createdAt: string;
	lastLogin: string;
	extended: {
		firstName: string;
		lastName: string;
	};
	buddy?: {
		buddy?: {
			codeName: string;
		};
	};
	subscriptions?: any[];
	onboardingProfile?: any;
}

export const sleepScoreList = [
	{
		value: 5,
		name: 'Fantastic',
		description: 'I sleep really well!',
	},
	{
		value: 4,
		name: 'Very good',
		description: 'I sleep well most nights',
	},
	{
		value: 3,
		name: 'Okay',
		description: 'It’s a bit up and down',
	},
	{
		value: 2,
		name: 'Not great',
		description: 'I often feel tired',
	},
	{
		value: 1,
		name: 'Terrible',
		description: 'I barely sleep and feel exhausted',
	},
];

export const TEXTS = [
	{ text: 'Struggling – Things have been tough lately', value: 1 },
	{ text: 'Not great – It’s been hard to stay positive', value: 2 },
	{ text: 'Just okay – I’ve felt a bit off lately', value: 3 },
	{ text: 'Pretty good – Things have been going well', value: 4 },
	{ text: 'Great – I’ve been feeling really good!', value: 5 },
];

function UserProfileScreen() {
	const { id } = useParams<{ id: string }>();
	const navigate = useNavigate();
	const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
	const [showOnboardingProfile, setShowOnboardingProfile] = useState(false);
	const [showUserPlanModal, setShowUserPlanModal] = useState(false);
	const [showUserJourney, setShowUserJourney] = useState(false);
	const [hoveredButton, setHoveredButton] = useState(false);
	const [editUserModal, setEditUserModal] = useState(false);
	const [changeSubscriptionModal, setChangeSubscriptionModal] = useState(false);
	const [deleteUserModal, setDeleteUserModal] = useState(false);
	const [isDeletingUser, setIsDeletingUser] = useState(false);

	console.log(userProfile);

	const loadUser = useCallback(async (userId: string) => {
		const resp = await getSingleUserById(userId);
		if (resp.status === 200) {
			setUserProfile(resp.data);
		}
	}, []);

	useEffect(() => {
		if (!id) return;
		loadUser(id);
	}, [id, loadUser]);

	const reloadUserProfile = useCallback(() => {
		if (!id) return;
		loadUser(id);
	}, [id, loadUser]);

	const editProfile = (): void => {
		setEditUserModal(true);
	};

	const deleteProfile = (): void => {
		setDeleteUserModal(true);
	};

	const changeSubscription = (): void => {
		setChangeSubscriptionModal(true);
	};

	const closeEditUserModal = () => {
		setEditUserModal(false);
	};

	const closeChangeSubscription = () => {
		setChangeSubscriptionModal(false);
	};

	const removeUser = async () => {
		if (!userProfile?.id) return;

		try {
			setIsDeletingUser(true);
			const resp = await deleteUser(userProfile.id);
			if (resp.status === 200) {
				setDeleteUserModal(false);
				navigate('/user-management');
			}
		} catch (error) {
			console.error('Failed to delete user', error);
		} finally {
			setIsDeletingUser(false);
		}
	};

	const closeDeleteUserModal = () => {
		setDeleteUserModal(false);
	};

	const buddyCodeName = userProfile?.buddy?.buddy?.codeName?.toLowerCase();
	const buddyImage = buddyCodeName ? buddyFaces[buddyCodeName]?.[4] : null;
	const editableUser = userProfile && {
		id: userProfile.id,
		email: userProfile.email,
		firstName: userProfile.extended.firstName,
		lastName: userProfile.extended.lastName,
	};

	return (
		<div id='scroll-inf-task' className='flex h-screen flex-col items-center overflow-y-auto bg-[#ECEBF8] py-[40px]'>
			<div className='w-full max-w-[1200px] px-[10px]'>
				<div className='flex min-h-[calc(100vh-80px)] w-full flex-col rounded-[10px] bg-white px-[40px] py-[30px]'>
					<div className='flex flex-row '>
						<div className=' flex cursor-pointer mr-5 items-center justify-center bg-primaryViolet hover:bg-darkViolet  min-w-[56px] w-[56px] h-[56px] rounded-full' onClick={() => navigate(-1)} onMouseEnter={() => setHoveredButton(true)} onMouseLeave={() => setHoveredButton(false)}>
							<TargetUndo fill={hoveredButton ? '#FDDF6D' : '#FFFFFF'} />
						</div>
						<div className='mb-[30px] flex-1 flex flex-col items-center'>
							<p className='text-[40px] font-bold leading-[50px] text-darkViolet'>User profile</p>
							<p className='mt-[8px] text-[14px] font-medium leading-5 text-mudPurple'>Manage user data</p>
						</div>
						<div className=' flex cursor-pointer mr-5 items-center justify-center  min-w-[56px] w-[56px] h-[56px] rounded-full invisible'></div>
					</div>
					<div className='flex flex-row '>
						<div className='flex flex-row items-center  gap-3'>
							<div className='min-w-[60px] h-[60px] rounded-full bg-lightPurpleGray border border-gray600 flex items-center justify-center overflow-hidden'>{buddyImage ? <img src={buddyImage} alt='User Buddy' width={40} height={40} /> : <FaUser size={30} color='white' />}</div>
							<div>
								<p className='text-gray900 font-semibold text-[16px] leading-5'>
									{userProfile?.extended.firstName}&nbsp;
									{userProfile?.extended.lastName}
								</p>

								<p className='text-gray600 font-medium text-[16px] leading-5'>{userProfile?.email}</p>
							</div>
						</div>
					</div>

					<div className='border-b border-b-gray300 mt-5'></div>
					{userProfile?.subscriptions && (
						<>
							<div className='flex flex-col justify-between mt-5'>
								<p className='text-gray600 font-semibold text-[16px] leading-5'>Subscriptions</p>
								<div className='flex flex-col gap-1 mt-2'>
									{userProfile.subscriptions?.map((item: any) => (
										<div className='flex flex-row gap-2'>
											<div className='bg-blue50 flex w-fit rounded-[20px] border border-blue200 px-2 py-[2px]'>
												<p className='text-blue700 font-medium text-[14px] leading-5'>{item?.subscription?.label}</p>
											</div>
											<span>
												{item.status}

												{item.status == 'active' && (
													<span>
														{' '}
														until:{' '}
														{new Date(item.endDate).toLocaleDateString('en-US', {
															year: 'numeric',
															month: 'short',
															day: 'numeric',
														})}
													</span>
												)}
											</span>
										</div>
									))}
									{!userProfile?.subscriptions?.length && (
										<div className='bg-greyBackground flex w-fit rounded-[20px] border border-greyMedium px-2 py-[2px] cursor-pointer'>
											<p className='text-greyStrong font-medium text-[14px] leading-5'>Free subscription</p>
										</div>
									)}
								</div>
							</div>
							<div className='border-b border-b-gray300 mt-5'></div>
						</>
					)}

					<div className='flex flex-row justify-between mt-5'>
						<p className='text-gray600 font-semibold text-[16px] leading-5'>Created date</p>
						<p className='text-gray600 font-medium text-[16px] leading-5'>
							{userProfile?.createdAt
								? new Date(userProfile.createdAt).toLocaleDateString('en-US', {
										year: 'numeric',
										month: 'short',
										day: 'numeric',
									})
								: '—'}
						</p>
					</div>
					<div className='border-b border-b-gray300 mt-5'></div>
					<div className='flex flex-row justify-between mt-5'>
						<p className='text-gray600 font-semibold text-[16px] leading-5'>Last active</p>
						<p className='text-gray600 font-medium text-[16px] leading-5'>
							{userProfile?.lastLogin
								? new Date(userProfile.lastLogin).toLocaleDateString('en-US', {
										year: 'numeric',
										month: 'short',
										day: 'numeric',
									})
								: '—'}
						</p>
					</div>
					<div className='border-b border-b-gray300 mt-5'></div>
					<div className='flex  flex-row gap-4 py-5'>
						<button className='bg-[#ECEBF8] p-[10px] rounded-[10px] cursor-pointer text-primaryViolet font-bold text-[14px] hover:text-brandYellow hover:bg-primaryViolet ease-in-out duration-200 transition-all' onClick={() => setShowOnboardingProfile(true)} type='button'>
							Onboarding profile
						</button>
						<button className='bg-[#ECEBF8] p-[10px] rounded-[10px] cursor-pointer text-primaryViolet font-bold text-[14px] hover:text-brandYellow hover:bg-primaryViolet ease-in-out duration-200 transition-all' onClick={() => setShowUserJourney(true)} type='button'>
							User journey
						</button>
						<button className='bg-[#ECEBF8] p-[10px] rounded-[10px] cursor-pointer text-primaryViolet font-bold text-[14px] hover:text-brandYellow hover:bg-primaryViolet ease-in-out duration-200 transition-all' onClick={() => setShowUserPlanModal(true)} type='button'>
							User plan
						</button>
						<button className='bg-[#ECEBF8] p-[10px] rounded-[10px] cursor-pointer text-primaryViolet font-bold text-[14px] hover:text-brandYellow hover:bg-primaryViolet ease-in-out duration-200 transition-all' onClick={() => setShowOnboardingProfile(true)} type='button'>
							User insights
						</button>
					</div>
					<div className='border-b border-b-gray300 mt-5'></div>
					<div className='flex flex-row justify-end gap-3 mt-10'>
						<div className='flex flex-row gap-2 items-center rounded-[6px] bg-[#EEF4FF] border border-[#C7D7FE] hover:border-[#3538CD]  p-[6px] cursor-pointer transition' onClick={() => changeSubscription()}>
							<FaCreditCard width={20} height={20} color='#3538CD' />
							<p className='text-[#3538CD] font-semibold text-[14px] leading-5'>Change subscription</p>
						</div>
						<div className='flex flex-row gap-2 items-center rounded-[6px] bg-[#EEF4FF] border border-[#C7D7FE] hover:border-[#3538CD]  p-[6px] cursor-pointer transition' onClick={() => editProfile()}>
							<FaEdit width={20} height={20} color='#3538CD' />
							<p className='text-[#3538CD] font-semibold text-[14px] leading-5'>Edit profile</p>
						</div>
						<div className='flex flex-row gap-2 items-center rounded-[6px] bg-[#EEF4FF] border hover:border-[#3538CD] border-[#C7D7FE]  p-[6px] cursor-pointer transition' onClick={() => deleteProfile()}>
							<FaTrash width={20} height={20} color='#3538CD' />
							<p className='text-[#3538CD] font-semibold text-[14px] leading-5'>Delete profile</p>
						</div>
					</div>
				</div>
			</div>
			<UserPlanModal isOpen={showUserPlanModal} closeModal={() => setShowUserPlanModal(false)} userProfile={userProfile} />
			<UserJourneyPreview isOpen={showUserJourney} closeModal={() => setShowUserJourney(false)} userProfile={userProfile} />
			<UserOnboardingProfileModal isOpen={showOnboardingProfile} closeModal={() => setShowOnboardingProfile(false)} userProfile={userProfile} />
			<ConfirmationModal modalIsOpen={deleteUserModal} closeModal={closeDeleteUserModal} confirmation={removeUser} message={'Do you want to delete this user?'} subMessage='The user will be permanently deleted.' loading={isDeletingUser} />
			<CreateEditUserModal modalIsOpen={editUserModal} closeModal={closeEditUserModal} user={editableUser} onSuccess={reloadUserProfile} />
			<ChangeSubscriptionModal modalIsOpen={changeSubscriptionModal} closeModal={closeChangeSubscription} user={userProfile} reloadList={reloadUserProfile} />
		</div>
	);
}

export default UserProfileScreen;
